import { Injectable } from '@angular/core';
import { SecurityQuestionModel, SecurityQuestionTree } from '../../../../../models';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  SecurityQuestionLoad,
  SecurityQuestionsInitialState,
  SecurityQuestionAdd,
  SecurityQuestionDelete,
  SecurityQuestionUpdate,
  SecurityQuestionSetType,
  SecurityQuestionSetId,
  SecurityQuestionSavePositions,
} from './security-check.actions';
import { SecurityCheckService } from './security-check.service';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { SecurityCheckContextType } from '../../../../../constants';
import { Error, CustomHttpErrorResponse } from '../../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface SecurityQuestionsListModel {
  type: SecurityCheckContextType;
  id: number;
  questionTree: SecurityQuestionTree;
  loaded: boolean;
}

const initialState: SecurityQuestionsListModel = {
  questionTree: null,
  type: null,
  id: null,
  loaded: false,
};

@State<SecurityQuestionsListModel>({
  name: 'hubSecurityCheckQuestionsList',
  defaults: initialState,
})
@Injectable()
export class SecurityCheckState {
  constructor(private securityCheckService: SecurityCheckService, private notificationService: NotificationService) {}

  @Selector()
  static getState(state: SecurityQuestionsListModel): SecurityQuestionsListModel {
    return state;
  }

  @Selector()
  static getLoaded(state: SecurityQuestionsListModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getType(state: SecurityQuestionsListModel): SecurityCheckContextType {
    return state.type;
  }

  @Selector()
  static getId(state: SecurityQuestionsListModel): number {
    return state.id;
  }

  @Selector()
  static getQuestionsTree(state: SecurityQuestionsListModel): SecurityQuestionTree {
    return state.questionTree;
  }

  @Action(SecurityQuestionLoad)
  getHubSecurityCheckQuestionsList(
    ctx: StateContext<SecurityQuestionsListModel>,
    { payload }: SecurityQuestionLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | SecurityQuestionsListModel> {
    return this.securityCheckService.getSecurityQuestionsList({ ...payload, type: ctx.getState().type }).pipe(
      map((questionTree: SecurityQuestionTree) => {
        return ctx.patchState({ questionTree, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SecurityQuestionSetType)
  setSecurityCheckType(ctx: StateContext<SecurityQuestionsListModel>, { payload }: SecurityQuestionSetType): void {
    ctx.patchState({ type: payload });
  }

  @Action(SecurityQuestionSetId)
  setSecurityCheckId(ctx: StateContext<SecurityQuestionsListModel>, { payload }: SecurityQuestionSetId): void {
    ctx.patchState({ id: payload });
  }

  @Action(SecurityQuestionAdd)
  addHubSecurityCheckQuestion(
    ctx: StateContext<SecurityQuestionsListModel>,
    { payload: { id, question, dialog } }: SecurityQuestionAdd,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | SecurityQuestionModel> {
    const { group, type } = question;

    return this.securityCheckService.addSecurityQuestion(id, question, ctx.getState().type).pipe(
      tap((securityQuestion: SecurityQuestionModel) => {
        dialog.close();

        const questionTree: SecurityQuestionTree = ctx.getState().questionTree;
        const questions = [...(questionTree[type]?.[group] || []), securityQuestion];

        ctx.patchState({
          questionTree: {
            ...questionTree,
            [type]: {
              ...questionTree?.[type],
              [group]: questions,
            },
          },
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SecurityQuestionUpdate)
  updateHubSecurityCheckQuestion(
    ctx: StateContext<SecurityQuestionsListModel>,
    { payload: { question, dialog } }: SecurityQuestionUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | SecurityQuestionsListModel> {
    const { group, type } = question;

    return this.securityCheckService.updateSecurityQuestion(question).pipe(
      map((securityQuestion: SecurityQuestionModel) => {
        dialog.close();
        const questionTree: SecurityQuestionTree = ctx.getState().questionTree;
        let questions: SecurityQuestionModel[] = questionTree[type][group];
        questions = questions.map((item: SecurityQuestionModel) =>
          item.id === securityQuestion.id ? securityQuestion : item,
        );

        return ctx.patchState({
          questionTree: {
            ...questionTree,
            [type]: {
              ...questionTree[type],
              [group]: questions,
            },
          },
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SecurityQuestionDelete)
  deleteHubSecurityCheckQuestion(
    ctx: StateContext<SecurityQuestionsListModel>,
    { payload: { questionId, dialog, group, type } }: SecurityQuestionDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const contextType = ctx.getState().type;

    return this.securityCheckService.deleteSecurityQuestion(questionId, contextType).pipe(
      map(() => {
        dialog.close();

        const questionTree: SecurityQuestionTree = ctx.getState().questionTree;
        let questions: SecurityQuestionModel[] = questionTree[type][group];

        questions = questions.filter((item: SecurityQuestionModel) => item.id !== questionId);

        ctx.patchState({
          questionTree: {
            ...questionTree,
            [type]: {
              ...questionTree[type],
              [group]: questions,
            },
          },
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SecurityQuestionSavePositions)
  saveQuestionsPosition(
    ctx: StateContext<SecurityQuestionsListModel>,
    { questions }: SecurityQuestionSavePositions,
  ): void {
    const questionIds: number[] = this.getIdsFromQuestionsArray(questions);

    this.securityCheckService.saveQuestionsPosition(questionIds).subscribe();
  }

  @Action(SecurityQuestionsInitialState)
  setInitialState(ctx: StateContext<SecurityQuestionsListModel>): void {
    ctx.setState(initialState);
  }

  private getIdsFromQuestionsArray(questions: SecurityQuestionModel[]): number[] {
    return questions.map((question: SecurityQuestionModel) => question.id);
  }
}
